<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import type { CameraZone } from '../types/camera-zones';
	import type { HandlePart, ZoneDrag, ZoneFill, ZonePoint, ZoneRect } from '../types/canvas-zones';
	import {
		clamp,
		clampDraggedCorner,
		cornerPoint,
		darkenColor,
		handleParts,
		hitTestCorner,
		insetPart,
		isInsideRect,
		moveRect,
		oppositeCorner,
		rectFromCoordinates,
		rectFromCorners,
		rectToCoordinates,
	} from '../helpers/canvas-helpers';
	import { rgbToCss } from 'src/helpers/utilities';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	export let zone: CameraZone | undefined;
	export let zones: CameraZone[] = [];
	export let isPrivacy: boolean = false;
	export let disabled: boolean = false;

	/**
	 * Handle metrics in CSS pixels. Touch needs a bigger disc and a grab area
	 * wider than the disc itself; a mouse does not.
	 */
	const HANDLE_METRICS = {
		fine: { radius: 16, hitRadius: 24 },
		coarse: { radius: 16, hitRadius: 26 },
	};
	const GLYPH_RATIO = 0.75;
	const RING_RATIO = 0.15;
	const HANDLE_GLYPH = '\uf424';
	const HANDLE_GLYPH_FONT_WEIGHT = 900;
	const HANDLE_GLYPH_FONT_FAMILY = '"Font Awesome 7 Pro"';
	const HANDLE_COLOR = '#ffffff';
	const HANDLE_ACTIVE_COLOR = '#3399ff';
	const ZONE_LINE_WIDTH = 2;
	/** Used until --radius-big can be read off the element. */
	const FALLBACK_FRAME_RADIUS = 12;

	let canvasEl: HTMLCanvasElement;
	let ctx: CanvasRenderingContext2D | null = null;
	let rafId: number | null = null;

	let windowWidth = 0;
	let cssWidth = 0;
	let cssHeight = 0;
	/** Corner radius the holder clips to, which the handle nests into. */
	let frameRadius = FALLBACK_FRAME_RADIUS;

	let editRect: ZoneRect | null = null;
	let viewRects: ZoneFill[] = [];

	let drag: ZoneDrag | null = null;
	/** Live position of the corner being resized, which may have flipped past the anchor. */
	let dragPoint: ZonePoint | null = null;
	let pointerPosition: ZonePoint | null = null;
	/** Set from the first pointer that touches the canvas; more reliable than the viewport. */
	let pointerTypeSeen: string | null = null;

	$: isViewMode = zones && zones.length > 0;
	$: isInteractive = !isViewMode && !disabled;
	/** The viewport is only the opening guess; the pointer that actually shows up wins. */
	$: coarsePointer = pointerTypeSeen
		? pointerTypeSeen !== 'mouse'
		: windowWidth <= SystemScreenBreakpoints.MOBILE_MAX;

	$: editFill = getEditFill(zone, isPrivacy);

	$: if (isViewMode) {
		viewRects = zones.map((z) => ({
			rect: rectFromCoordinates(z.coordinates),
			fill: rgbToCss(z.color, 0.2),
		}));
		scheduleDraw();
	}

	// Geometry is re-derived from the zone except mid-gesture, where the drag owns it.
	$: if (!isViewMode && zone && editFill) {
		if (!drag) editRect = rectFromCoordinates(zone.coordinates);
		scheduleDraw();
	}

	$: if (ctx && cssWidth && cssHeight) syncBackingStore(cssWidth, cssHeight);

	const getEditFill = (editZone: CameraZone | undefined, privacy: boolean): string => {
		if (!editZone) return '';

		return privacy ? rgbToCss(darkenColor(editZone.color), 0.7) : rgbToCss(editZone.color, 0.2);
	};

	const metricsFor = (coarse: boolean) => (coarse ? HANDLE_METRICS.coarse : HANDLE_METRICS.fine);

	const readFrameRadius = (): number => {
		if (!canvasEl) return FALLBACK_FRAME_RADIUS;

		const declared = Number.parseFloat(getComputedStyle(canvasEl).getPropertyValue('--radius-big'));

		return Number.isFinite(declared) ? declared : FALLBACK_FRAME_RADIUS;
	};

	/** Matches the bitmap to the displayed size, so drawing can stay in CSS pixels. */
	const syncBackingStore = (width: number, height: number) => {
		if (!canvasEl || !ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const nextWidth = Math.round(width * dpr);
		const nextHeight = Math.round(height * dpr);

		if (canvasEl.width !== nextWidth || canvasEl.height !== nextHeight) {
			canvasEl.width = nextWidth;
			canvasEl.height = nextHeight;
		}

		// Resizing the backing store resets context state, so the scale is reapplied here.
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		drawZones();
	};

	/** Coalesces every request in a frame into one draw of the newest pointer sample. */
	const scheduleDraw = () => {
		if (rafId !== null) return;

		rafId = requestAnimationFrame(() => {
			rafId = null;
			applyDrag();
			drawZones();
		});
	};

	const drawZoneShape = (rect: ZoneRect, fill: string, outline: string | null) => {
		if (!ctx) return;

		const x = rect.x1 * cssWidth;
		const y = rect.y1 * cssHeight;
		const width = (rect.x2 - rect.x1) * cssWidth;
		const height = (rect.y2 - rect.y1) * cssHeight;

		ctx.beginPath();
		ctx.rect(x, y, width, height);
		ctx.fillStyle = fill;
		ctx.fill();

		if (!outline) return;

		ctx.lineWidth = ZONE_LINE_WIDTH;
		ctx.strokeStyle = outline;
		ctx.stroke();
	};

	/** Rounded rectangle with a radius per corner. arcTo keeps this portable. */
	const tracePart = (part: HandlePart) => {
		if (!ctx) return;

		const { x, y, width, height, radii } = part;
		const limit = Math.min(width, height) / 2;
		const [topLeft, topRight, bottomRight, bottomLeft] = radii.map((radius) =>
			clamp(radius, 0, limit),
		);

		ctx.beginPath();
		ctx.moveTo(x + topLeft, y);
		ctx.arcTo(x + width, y, x + width, y + height, topRight);
		ctx.arcTo(x + width, y + height, x, y + height, bottomRight);
		ctx.arcTo(x, y + height, x, y, bottomLeft);
		ctx.arcTo(x, y, x + width, y, topLeft);
		ctx.closePath();
	};

	const drawHandle = (x: number, y: number, isActive: boolean) => {
		if (!ctx) return;

		const { radius } = metricsFor(coarsePointer);
		const parts = handleParts(x, y, radius, cssWidth, cssHeight, frameRadius);
		const [core] = parts;

		ctx.fillStyle = HANDLE_COLOR;
		for (const part of parts) {
			tracePart(part);
			ctx.fill();
		}

		if (!isActive) return;

		const ring = radius * RING_RATIO;
		tracePart(insetPart(core, ring / 2));
		ctx.lineWidth = ring;
		ctx.strokeStyle = HANDLE_ACTIVE_COLOR;
		ctx.stroke();

		// Sized off the core rather than the radius, so the glyph keeps its
		// proportions while the core is seating itself into a corner.
		const glyphSize = (Math.min(core.width, core.height) / 2) * GLYPH_RATIO;

		ctx.save();
		ctx.translate(core.x + core.width / 2, core.y + core.height / 2);
		ctx.rotate(Math.PI / 2);
		ctx.fillStyle = HANDLE_ACTIVE_COLOR;
		ctx.font = `${HANDLE_GLYPH_FONT_WEIGHT} ${glyphSize}px ${HANDLE_GLYPH_FONT_FAMILY}`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(HANDLE_GLYPH, 0, 0);
		ctx.restore();
	};

	/**
	 * Mid-resize the handles track the gesture, so they stay on the dragged
	 * diagonal even once the zone has flipped past the anchor. At rest they sit
	 * on the top-left and bottom-right corners.
	 */
	const drawZones = () => {
		if (!ctx || !cssWidth || !cssHeight) return;

		ctx.clearRect(0, 0, cssWidth, cssHeight);

		if (isViewMode) {
			viewRects.forEach((z) => drawZoneShape(z.rect, z.fill, null));
			return;
		}

		if (!zone || !editRect) return;

		drawZoneShape(editRect, editFill, rgbToCss(zone.color, 1));

		if (drag?.anchor && dragPoint) {
			drawHandle(drag.anchor.x * cssWidth, drag.anchor.y * cssHeight, false);
			drawHandle(dragPoint.x * cssWidth, dragPoint.y * cssHeight, true);
			return;
		}

		drawHandle(editRect.x1 * cssWidth, editRect.y1 * cssHeight, false);
		drawHandle(editRect.x2 * cssWidth, editRect.y2 * cssHeight, false);
	};

	const applyDrag = () => {
		if (!drag || !pointerPosition || !editRect) return;

		const x = pointerPosition.x / cssWidth;
		const y = pointerPosition.y / cssHeight;

		if (drag.anchor) {
			dragPoint = clampDraggedCorner(drag.anchor, x, y);
			editRect = rectFromCorners(drag.anchor, dragPoint);
			return;
		}

		editRect = moveRect(editRect, x, y, drag.grab_offset_x, drag.grab_offset_y);
	};

	const updateHoverCursor = (e: PointerEvent) => {
		if (e.pointerType !== 'mouse' || !canvasEl) return;

		if (!isInteractive || !editRect) {
			canvasEl.style.cursor = '';
			return;
		}

		const corner = hitTestCorner(
			editRect,
			e.offsetX,
			e.offsetY,
			cssWidth,
			cssHeight,
			metricsFor(false).hitRadius,
		);

		if (corner) {
			canvasEl.style.cursor = 'nwse-resize';
		} else if (isInsideRect(editRect, e.offsetX / cssWidth, e.offsetY / cssHeight)) {
			canvasEl.style.cursor = 'move';
		} else {
			canvasEl.style.cursor = '';
		}
	};

	const handlePointerDown = (e: PointerEvent) => {
		if (!isInteractive || !editRect || drag) return;
		if (e.pointerType === 'mouse' && e.button !== 0) return;

		pointerTypeSeen = e.pointerType;

		const bounds = canvasEl.getBoundingClientRect();
		const x = e.clientX - bounds.left;
		const y = e.clientY - bounds.top;

		// Taken from this event rather than the reactive flag, which has not settled yet.
		const hitRadius = metricsFor(e.pointerType !== 'mouse').hitRadius;
		const corner = hitTestCorner(editRect, x, y, cssWidth, cssHeight, hitRadius);
		const insideBody = !corner && isInsideRect(editRect, x / cssWidth, y / cssHeight);

		if (!corner && !insideBody) return;

		// Seeded so the grabbed handle reads as active from the moment of contact.
		dragPoint = corner ? cornerPoint(editRect, corner) : null;

		drag = {
			pointer_id: e.pointerId,
			anchor: corner ? oppositeCorner(editRect, corner) : null,
			bounds,
			grab_offset_x: insideBody ? x / cssWidth - editRect.x1 : 0,
			grab_offset_y: insideBody ? y / cssHeight - editRect.y1 : 0,
		};

		// Capture keeps the gesture alive once the pointer leaves the canvas, which
		// is also why every coordinate above has to be clamped.
		canvasEl.setPointerCapture(e.pointerId);
		scheduleDraw();
	};

	const handlePointerMove = (e: PointerEvent) => {
		if (!drag) {
			updateHoverCursor(e);
			return;
		}

		if (e.pointerId !== drag.pointer_id) return;

		e.preventDefault();
		pointerPosition = { x: e.clientX - drag.bounds.left, y: e.clientY - drag.bounds.top };
		scheduleDraw();
	};

	const handleDragEnd = (e: PointerEvent) => {
		if (!drag || e.pointerId !== drag.pointer_id) return;

		applyDrag();

		// Cleared before releasing capture so the resulting lostpointercapture is a no-op.
		drag = null;
		dragPoint = null;
		pointerPosition = null;

		if (canvasEl?.hasPointerCapture(e.pointerId)) {
			canvasEl.releasePointerCapture(e.pointerId);
		}

		if (zone && editRect) zone.coordinates = rectToCoordinates(editRect);

		drawZones();
	};

	/** Suppresses the long-press callout over the video mid-drag. */
	const handleContextMenu = (e: MouseEvent) => {
		e.preventDefault();
		return false;
	};

	onMount(() => {
		ctx = canvasEl?.getContext('2d') ?? null;
		frameRadius = readFrameRadius();
	});

	onDestroy(() => {
		if (rafId !== null) cancelAnimationFrame(rafId);
	});
</script>

<svelte:window bind:innerWidth={windowWidth} />

<div class="zone-holder" class:disabled bind:clientWidth={cssWidth} bind:clientHeight={cssHeight}>
	<canvas
		bind:this={canvasEl}
		class={'zone-canvas ' + (isViewMode ? 'view' : 'edit')}
		on:pointerdown={handlePointerDown}
		on:pointermove={handlePointerMove}
		on:pointerup={handleDragEnd}
		on:pointercancel={handleDragEnd}
		on:lostpointercapture={handleDragEnd}
		on:contextmenu={handleContextMenu}
	></canvas>
	<slot />
</div>

<style>
	.zone-holder {
		position: relative;
		width: 100%;
		height: auto;
		aspect-ratio: 16 / 9;
		border-radius: var(--radius-big);
		overflow: hidden;
	}

	.zone-canvas {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 10;
		touch-action: none;
	}

	.zone-canvas.view {
		pointer-events: none;
	}

	.zone-holder.disabled {
		pointer-events: none;
	}
</style>
